import { cx } from './cx.js';

/** Initiales d'un nom (« Camille Laurent » → « CL »). */
export function initials(name: string): string {
  const words = name
    .trim()
    .split(/[\s-]+/)
    .filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

const TONES = ['author-garage', 'author-customer', 'author-support', 'author-system'] as const;
const TONE_CLASS: Record<(typeof TONES)[number], string> = {
  'author-garage': 'bg-author-garage-bg text-author-garage-fg',
  'author-customer': 'bg-author-customer-bg text-author-customer-fg',
  'author-support': 'bg-author-support-bg text-author-support-fg',
  'author-system': 'bg-author-system-bg text-author-system-fg',
};

/** Teinte stable déduite du nom (une même personne garde la même couleur). */
function toneFor(name: string): (typeof TONES)[number] {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TONES[h % TONES.length] ?? 'author-system';
}

/**
 * Avatar à initiales. Décoratif par défaut (le nom est écrit à côté) ; `label` pour l'annoncer.
 * `tone="brand"` : couleur du garage (bloc de marque sans logo).
 */
export function Avatar({
  name,
  size = 'md',
  tone,
  label,
  className,
}: {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  tone?: 'brand' | (typeof TONES)[number];
  label?: string;
  className?: string;
}) {
  const t = tone ?? toneFor(name);
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cx(
        'inline-grid shrink-0 place-items-center rounded-full font-medium tracking-wide select-none',
        { sm: 'size-8 text-xs', md: 'size-10 text-sm', lg: 'size-12 text-base' }[size],
        t === 'brand' ? 'bg-mark text-on-mark ring-1 ring-inset ring-black/10' : TONE_CLASS[t],
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}
